import { useEffect } from 'react';
import { SITE_URL } from '../config/productFacts';

/**
 * Google Merchant-konforme Zusatzfelder für ein `offers`-Schema.
 *
 * Löst die 3 typischen Search-Console-Warnungen bei Product/Offer-Schema:
 *   · Feld "hasMerchantReturnPolicy" fehlt
 *   · Feld "validFrom" fehlt
 *   · Feld "shippingDetails" fehlt
 *
 * Für digitale FuelX-Produkte:
 *   - 14 Tage gesetzliches Widerrufsrecht → MerchantReturnPolicy
 *   - validFrom: 2024-01-01 (statisch, weil Card kontinuierlich verfügbar)
 *   - shippingDetails: digitaler Versand — sofort, 0 €, DE + weitere EU-Länder
 */
export const MERCHANT_OFFER_EXTRAS = {
  validFrom: '2024-01-01',
  hasMerchantReturnPolicy: {
    '@type': 'MerchantReturnPolicy',
    applicableCountry: 'DE',
    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
    merchantReturnDays: 14,
    returnMethod: 'https://schema.org/ReturnByMail',
    returnFees: 'https://schema.org/FreeReturn',
    refundType: 'https://schema.org/FullRefund',
  },
  shippingDetails: {
    '@type': 'OfferShippingDetails',
    shippingRate: {
      '@type': 'MonetaryAmount',
      value: '0',
      currency: 'EUR',
    },
    shippingDestination: {
      '@type': 'DefinedRegion',
      addressCountry: ['DE', 'AT', 'CH', 'LU', 'BE', 'NL', 'FR', 'IT', 'ES', 'PL', 'CZ'],
    },
    deliveryTime: {
      '@type': 'ShippingDeliveryTime',
      handlingTime: {
        '@type': 'QuantitativeValue',
        minValue: 0,
        maxValue: 0,
        unitCode: 'DAY',
      },
      transitTime: {
        '@type': 'QuantitativeValue',
        minValue: 0,
        maxValue: 0,
        unitCode: 'DAY',
      },
    },
  },
};

/** Nur auf der Produktions-Domain darf indexiert werden (Preview/Staging: noindex via index.html). */
function isProductionHost() {
  const h = window.location.hostname;
  return h === 'fuelxcard.com' || h === 'www.fuelxcard.com';
}

function setMetaTag(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Setzt Titel + Meta-Description + Canonical + Open-Graph/Twitter-Tags + Robots pro Seite.
 * Canonical wird automatisch aus SITE_URL + Pfad gebildet (ohne Query-/Tracking-Parameter),
 * kann aber via options.canonical überschrieben werden.
 * Für Checkout-/interne Seiten: noindex=true → verhindert Indexierung.
 */
export function usePageMeta(title, description, options = {}) {
  const { canonical, noindex = false } = options;
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    if (meta && description) meta.setAttribute('content', description);

    // Canonical: immer selbstreferenzierend auf die saubere Produktions-URL (ohne Parameter)
    const cleanPath = window.location.pathname.replace(/\/{2,}/g, '/').replace(/(.)\/$/, '$1');
    const canonicalUrl = canonical || `${SITE_URL}${cleanPath}`;
    let linkEl = document.querySelector('link[rel="canonical"]');
    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.setAttribute('rel', 'canonical');
      document.head.appendChild(linkEl);
    }
    linkEl.setAttribute('href', canonicalUrl);

    // Open Graph + Twitter synchron halten
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);

    // Robots Meta — auf Nicht-Produktions-Hosts bleibt das globale noindex bestehen
    let robots = document.querySelector('meta[name="robots"]');
    const prevRobots = robots ? robots.getAttribute('content') : null;
    if (noindex) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow');
    }

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute('content', prevDesc);
      if (noindex && robots && isProductionHost()) {
        if (prevRobots !== null) robots.setAttribute('content', prevRobots);
        else robots.setAttribute('content', 'index, follow');
      }
    };
  }, [title, description, canonical, noindex]);
}

/**
 * Fügt JSON-LD Structured Data ins <head> ein — für Google Rich Snippets.
 * Beispiel: useStructuredData({ '@type': 'Product', ... })
 * Der Aufrufer sollte das schema-Objekt memoizen (useMemo), damit es stabil bleibt.
 */
export function useStructuredData(schema, id = 'jsonld-page') {
  useEffect(() => {
    if (!schema) return undefined;
    const scriptId = `structured-data-${id}`;
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      document.head.appendChild(script);
    }
    const payload = { '@context': 'https://schema.org', ...schema };
    script.textContent = JSON.stringify(payload);
    return () => {
      script?.parentNode?.removeChild(script);
    };
  }, [schema, id]);
}
